/**
 * The descriptor table: one canonical funnel step to its vendor counterparts.
 *
 * This file is the taxonomy. It imports nothing but the vocabulary, so it can
 * be read (and tested) as a table rather than as code.
 *
 * ga4       GA4 event name, or null when the step is not reported to GA4.
 * meta      Meta standard event name, or null.
 * ads       Google Ads conversion label key, or null. Two steps may share one
 *           label: Ads cannot segment a conversion action by a custom
 *           parameter, so one action per *action*, never one per funnel source.
 * adsTag    Whether the GA4 event is also addressed to the Ads tag itself (not
 *           a conversion). Only page_view: that is what Ads remarketing
 *           audiences are built from, and the old unscoped calls fed it.
 * queueable Whether the Meta event may be held while consent is undecided.
 */

import { EVENTS } from '../../constants/analytics';


export const ADS_LABELS = {
  BEGIN_CHECKOUT: 'BEGIN_CHECKOUT',
  CONTACT: 'CONTACT',
  PURCHASE: 'PURCHASE',
  SEARCH: 'SEARCH',
  SELECT_ROOM: 'SELECT_ROOM',
};

export const EVENT_DESCRIPTORS = {
  /* Not queueable: page views are the most frequent event, so held ones fill
     the buffer and starve the funnel steps behind them, and a replay would
     stamp twenty PageViews with the consent page's URL. The pixel gets one
     PageView for the page consent was given on when it loads (see
     updateConsent), which is what Meta's own snippet does on load. */
  [EVENTS.PAGE_VIEW]: {
    ga4: 'page_view', meta: 'PageView', ads: null, adsTag: true, queueable: false,
  },
  [EVENTS.SCROLL_DEPTH]: {
    ga4: 'scroll_depth_200px', meta: null, ads: null, queueable: false,
  },
  [EVENTS.CLICK_BOOK_NOW]: {
    ga4: 'click_book_now', meta: null, ads: null, queueable: false,
  },
  [EVENTS.CALENDAR_OPENED]: {
    ga4: 'click_calendar_opened', meta: null, ads: null, queueable: false,
  },
  [EVENTS.SEARCH_SUBMITTED]: {
    ga4: 'click_submit_booking_period', meta: 'Search', ads: ADS_LABELS.SEARCH, queueable: true,
  },
  [EVENTS.DIRECT_SEARCH_SUBMITTED]: {
    ga4: 'direct_booking_landing_submit', meta: 'Search', ads: ADS_LABELS.SEARCH, queueable: true,
  },
  [EVENTS.ROOM_VIEWED]: {
    ga4: 'view_item', meta: 'ViewContent', ads: null, queueable: true,
  },
  [EVENTS.DIRECT_ROOM_EXPANDED]: {
    ga4: 'direct_booking_room_expanded', meta: 'ViewContent', ads: null, queueable: true,
  },
  [EVENTS.RATE_SELECTED]: {
    ga4: 'click_book_a_room', meta: 'AddToCart', ads: ADS_LABELS.SELECT_ROOM, queueable: true,
  },
  [EVENTS.CHECKOUT_STARTED]: {
    ga4: 'begin_checkout', meta: 'InitiateCheckout', ads: ADS_LABELS.BEGIN_CHECKOUT, queueable: true,
  },
  [EVENTS.ADD_ON_ADDED]: {
    ga4: 'add_to_cart', meta: 'AddToCart', ads: null, queueable: true,
  },
  [EVENTS.DETAILS_SUBMITTED]: {
    ga4: 'click_continue_to_book', meta: 'AddPaymentInfo', ads: null, queueable: true,
  },
  /* Never queueable. If marketing consent is absent when the booking confirms,
     the Meta Purchase is dropped and the server skips the Conversions API for
     the same reason. A conversion replayed later under a stale event_id is
     worse than a missing one, and this is the event the banner promise is
     really about. */
  [EVENTS.PURCHASE]: {
    ga4: 'purchase', meta: 'Purchase', ads: ADS_LABELS.PURCHASE, queueable: false,
  },
  [EVENTS.REFUND]: {
    ga4: 'refund', meta: null, ads: null, queueable: false,
  },
  [EVENTS.CLICK_EMAIL]: {
    ga4: 'click_email_link', meta: null, ads: ADS_LABELS.CONTACT, queueable: false,
  },
  [EVENTS.CLICK_PHONE]: {
    ga4: 'click_phone_number', meta: null, ads: ADS_LABELS.CONTACT, queueable: false,
  },
};

/* booking_completed is deliberately absent from this table. It duplicated
   `purchase` for the same transaction, which is the double-count this refactor
   removes, but it is kept firing from the purchase path (see index.js) as a
   plain, non-conversion event until someone with GA4 and Ads access confirms
   nothing reports on it. See the deprecation note in the README. */
export const DEPRECATED_BOOKING_COMPLETED = 'booking_completed';
